import { createStore } from 'vuex'
import { CoralogixRum } from '@coralogix/browser'

import router from '../router'
import i18n from '../plugins/i18n'

import storesApi from '../api/storesApi'
import ordersApi from '@/api/ordersApi'
import customersApi from '@/api/customersApi'
import brandingDefault from '@/assets/defaults/branding.json'
import risk from './modules/risk'

// The service the picker would land on by default: a visible non-address one first, then any
// visible one. A service flagged `is_visible: false` (Dine-In is enabled chain-wide but only
// used for in-store loyalty orders) is skipped while a visible one exists, so the user is
// never left on a service the UI cannot switch off.
// Chains that hide EVERY service type are the last resort: without a selection nothing ever
// fetches the store list, which leaves the ordering page empty.
function pickDefaultService(services = []) {
  return (
    services.find((service) => !service.requires_address && service.is_visible) ||
    services.find((service) => service.is_visible) ||
    services[0] ||
    null
  )
}

export default createStore({
  state: {
    storeConfig: null,
    selectedService:
      localStorage.getItem('selectedServiceData') && localStorage.getItem('selectedServiceData') != 'undefined'
        ? JSON.parse(localStorage.getItem('selectedServiceData'))
        : null,
    loggedIn: false,
    selectedStore: null,
    orderData: null,
    phoneVerified: localStorage.getItem('phoneVerified') == 'true' ? true : false,
    emailVerified: localStorage.getItem('emailVerified') == 'true' ? true : false,
    selectedStoreDays: JSON.parse(localStorage.getItem('selectedStoreDays')) || null,
    selectedDay: JSON.parse(localStorage.getItem('selectedDay')) || null,
    selectedHour: JSON.parse(localStorage.getItem('selectedHour')) || null,
    selectedPaymentMethod: JSON.parse(localStorage.getItem('selectedPaymentMethod')) || null,
    buttonLoader: false,
    userEmail: localStorage.getItem('userEmail') || null,
    dayAndTimeSelected: localStorage.getItem('dayAndTimeSelected') == 'true' ? true : false,
    customerData: null,
    toaster: [],
    itemToaster: {
      message: '',
      show: false,
    },
    selectedAddress: JSON.parse(localStorage.getItem('selectedAddress')) || null,
    branding: null,
    cartIsOpen: false,
    // Reorder with items that won't carry over: the cart previews the past order until the
    // customer confirms (see reorderMixins.confirmReorderDiff)
    reorderPreview: null,
    cookies: JSON.parse(localStorage.getItem('cookies')) || {
      __ac: '',
      'NovaDine.session': '',
      ROUTE_ID: '',
    },
    copperDetails: null,
    cartButtonTop: null,
    cartButtonLeft: null,
    mapType: 'RADAR',
    autoCompleteType: 'RADAR',
    creditCardPaymentPayload: null,
    showLoader: false,
    appKey: 0,
    menuAllergenFilters: JSON.parse(localStorage.getItem('menuAllergenFilters')) || [],
    menuPreferenceFilters: JSON.parse(localStorage.getItem('menuPreferenceFilters')) || [],
  },
  mutations: {
    SET_STORE_CONFIG(state, storeConfig) {
      // `programs_information` is only populated for tier-based programs; integrations
      // like Punchh return a loyalty block without it.
      if (Array.isArray(storeConfig.loyalty?.programs_information)) {
        let tiers = storeConfig.loyalty.programs_information
        // sort the tiers by threshold from lowest to highest
        tiers.sort((a, b) => a.threshold - b.threshold)
        storeConfig.loyalty.programs_information = tiers
      }
      localStorage.setItem('chainName', storeConfig.chain_name)
      // Save include_out_of_stock setting to localStorage for use before config is loaded
      if (storeConfig.enable_menu_filters?.include_out_of_stock) {
        localStorage.setItem('include_out_of_stock', 'true')
      } else {
        localStorage.setItem('include_out_of_stock', 'false')
      }
      // Resolve map/autocomplete providers when config arrives so components
      // that mount before getStoreConfig resolves don't stay stuck on RADAR defaults.
      state.mapType = storeConfig.enable_google_maps_api ? 'GOOGLE' : 'RADAR'
      state.autoCompleteType = storeConfig.enable_google_places_api ? 'GOOGLE' : 'RADAR'
      state.storeConfig = storeConfig

      // Determine the active language. Priority:
      //   1. A guest's explicit selection persisted in localStorage (if still offered by this site)
      //   2. The backend-provided localization.default_language
      //   3. Legacy country_code mapping (for configs that don't return default_language yet)
      //   4. English fallback
      // FE only ships these locales; anything else falls back to English.
      const SUPPORTED_LOCALES = ['en', 'es', 'ru', 'kz', 'pt', 'de', 'fr', 'it', 'rm']
      // Switzerland has four national languages; German is the widest-reached default, and the
      // other three are offered through localization.languages.
      const countryToLocale = {
        US: 'en',
        ES: 'es',
        MX: 'es',
        RU: 'ru',
        KZ: 'kz',
        BR: 'pt',
        CH: 'de',
        DE: 'de',
        AT: 'de',
        FR: 'fr',
        IT: 'it',
      }
      const offeredLanguages = (storeConfig.localization?.languages || []).filter((lang) =>
        SUPPORTED_LOCALES.includes(lang)
      )

      const storedLanguage = localStorage.getItem('selectedLanguage')
      const defaultLanguage = storeConfig.localization?.default_language
      const legacyLanguage = countryToLocale[storeConfig.country_code]

      let activeLanguage = 'en'
      if (storedLanguage && offeredLanguages.includes(storedLanguage)) {
        activeLanguage = storedLanguage
      } else if (defaultLanguage && SUPPORTED_LOCALES.includes(defaultLanguage)) {
        activeLanguage = defaultLanguage
      } else if (legacyLanguage) {
        activeLanguage = legacyLanguage
      }

      i18n.global.locale.value = activeLanguage
    },
    SET_MENU_FILTERS(state, { allergens, preferences }) {
      state.menuAllergenFilters = allergens || []
      state.menuPreferenceFilters = preferences || []
      localStorage.setItem('menuAllergenFilters', JSON.stringify(state.menuAllergenFilters))
      localStorage.setItem('menuPreferenceFilters', JSON.stringify(state.menuPreferenceFilters))
    },
    SET_SELECTED_SERVICE(state, selectedService) {
      state.selectedService = selectedService
      // set the selected service in local storage
      localStorage.setItem('selectedServiceData', JSON.stringify(selectedService))
    },
    SET_LOGGED_IN(state, loggedIn) {
      localStorage.setItem('loggedIn', loggedIn)
      state.loggedIn = loggedIn
    },
    SET_SELECTED_STORE(state, selectedStore) {
      state.selectedStore = selectedStore
    },
    SET_ORDER_DATA(state, orderData) {
      state.orderData = orderData
    },
    SET_PHONE_VERIFIED(state, phoneVerified) {
      state.phoneVerified = phoneVerified
    },
    SET_EMAIL_VERIFIED(state, emailVerified) {
      state.emailVerified = emailVerified
    },
    SET_SELECTED_STORE_DAYS(state, selectedStoreDays) {
      state.selectedStoreDays = selectedStoreDays
    },
    SET_SELECTED_DAY(state, selectedDay) {
      state.selectedDay = selectedDay
    },
    SET_SELECTED_HOUR(state, selectedHour) {
      state.selectedHour = selectedHour
    },
    SET_SELECTED_PAYMENT_METHOD(state, selectedPaymentMethod) {
      state.selectedPaymentMethod = selectedPaymentMethod
    },
    SET_BUTTON_LOADER(state, buttonLoader) {
      state.buttonLoader = buttonLoader
    },
    SET_USER_EMAIL(state, userEmail) {
      state.userEmail = userEmail
    },
    SET_DAY_AND_TIME_SELECTED(state, dayAndTimeSelected) {
      state.dayAndTimeSelected = dayAndTimeSelected
    },
    SET_CUSTOMER_DATA(state, customerData) {
      state.customerData = customerData
    },
    SET_TOASTER(state, payload) {
      payload.time = Date().now
      state.toaster.push(payload)
      // after 3 seconds remove the toaster
      setTimeout(() => {
        state.toaster.shift()
      }, payload.time || 3000)
    },
    SET_ITEM_TOASTER(state, payload) {
      state.itemToaster = payload
      setTimeout(() => {
        state.itemToaster.show = false
      }, payload.time || 3000)
    },
    SET_SELECTED_ADDRESS(state, selectedAddressId) {
      state.selectedAddress = selectedAddressId
    },
    SET_BRANDING(state, payload) {
      state.branding = payload
    },
    SET_BRANDING_DEFAULT(state, payload) {
      if (payload.key.includes('.')) {
        let keys = payload.key.split('.')
        state.branding[keys[0]][keys[1]] = payload.value
      } else {
        state.branding[payload.key] = payload.value
      }
    },
    SET_CART_IS_OPEN(state, payload) {
      state.cartIsOpen = payload
    },
    SET_REORDER_PREVIEW(state, payload) {
      state.reorderPreview = payload
    },
    SET_COOKIES(state, payload) {
      // Legacy gateway used to return cookies in the response body.
      // With /api proxy, cookies come via Set-Cookie headers (often HttpOnly) and are not readable in JS.
      // So `payload` is frequently undefined now; this must be a safe no-op.
      if (payload && typeof payload === 'object') {
        if (payload.__ac) {
          state.cookies.__ac = payload.__ac
        }
        if (payload['NovaDine.session']) {
          state.cookies['NovaDine.session'] = payload['NovaDine.session']
        }
        if (payload.ROUTE_ID) {
          state.cookies.ROUTE_ID = payload.ROUTE_ID
        }
        localStorage.setItem('cookies', JSON.stringify(state.cookies))
      }
    },
    RESET_COOKIES(state) {
      state.cookies = { __ac: '', 'NovaDine.session': '', ROUTE_ID: '' }
      localStorage.setItem('cookies', JSON.stringify(state.cookies))
    },
    PURGE_STALE_DATA(state) {
      // Deliberately an allowlist rather than a list of keys to delete. The old delete-list
      // silently fell behind as the app grew: `selectedServiceId` was never on it, so a
      // service type picked days earlier survived every purge and kept being sent to the
      // API. Dropping unknown keys by default is the safe direction for order state — a
      // forgotten key now means "cleared", not "kept forever".
      const KEYS_TO_KEEP = [
        // Login and identity: the user must stay signed in across a purge.
        'auth',
        'loggedIn',
        'customerId',
        'userEmail',
        'phoneVerified',
        'emailVerified',
        'cookies',
        // Not order state: `version` drives the full storage reset in App.vue#checkVersion
        // and must not be disturbed, and `last_api_request` is the clock that schedules
        // this purge in the first place.
        'version',
        'last_api_request',
      ]

      try {
        Object.keys(localStorage)
          .filter((key) => !KEYS_TO_KEEP.includes(key))
          .forEach((key) => localStorage.removeItem(key))
      } catch (e) {
        console.warn('[PURGE_STALE_DATA] Failed to clear localStorage:', e)
      }

      // Reset the Vuex state that mirrors the cleared keys, otherwise the old order would
      // survive in memory even though its storage is gone.
      state.orderData = null
      state.dayAndTimeSelected = false
      state.selectedDay = null
      state.selectedHour = null
      state.selectedStore = null
      state.selectedStoreDays = null
      state.selectedAddress = null
      state.selectedPaymentMethod = null
      state.creditCardPaymentPayload = null
      state.copperDetails = null
      state.menuAllergenFilters = []
      state.menuPreferenceFilters = []
      state.showLoader = false
      state.buttonLoader = false

      // Re-seed a usable service type immediately. A mid-session purge cannot rely on
      // `getSelectedService` running again — `getStoreConfig` returns early once the config
      // is loaded — and HomeView#ensureStoresLoaded refuses to load stores without a
      // selected service, which would leave the user on an empty home page.
      const defaultService = pickDefaultService(state.storeConfig?.services)
      state.selectedService = defaultService
      if (defaultService) {
        localStorage.setItem('selectedServiceId', defaultService.service_type_id)
        localStorage.setItem('selectedServiceData', JSON.stringify(defaultService))
      }

      // Increment appKey to force a re-render of the main application components.
      // This effectively closes all local modals and resets local loading states.
      state.appKey++
    },
    SET_COPPER_DETAILS(state, payload) {
      state.copperDetails = payload
    },
    SET_CART_BUTTON_POSITION(state, payload) {
      state.cartButtonTop = payload.top
      state.cartButtonLeft = payload.left
    },
    SET_AUTO_COMPLETE_TYPE(state, autoCompleteType) {
      state.autoCompleteType = autoCompleteType;
    },
    SET_MAP_TYPE(state, mapType) {
      state.mapType = mapType;
    },
    SET_LOYALTY_BASE_URL(state, newUrl) {
      state.loyaltyBaseUrl = newUrl;
    },
    SET_LOYALTY_CLIENT_ID(state, newUrl) {
      state.loyaltyClientId = newUrl;
    },
    SET_CREDIT_CARD_PAYMENT_PAYLOAD(state, payload) {
      state.creditCardPaymentPayload = payload;
    },
    SET_SHOW_LOADER(state, showLoader) {
      state.showLoader = showLoader;
    }
  },
  actions: {
    getStoreConfig({ commit, dispatch }) {
      if (this.state.storeConfig) return
      storesApi
        .getConfiguration()
        .then((response) => {
          const configData = response?.data?.data || {}
          const services = Array.isArray(configData.services) ? configData.services : []

          const loyaltyBaseUrl = configData?.foreign_auth_providers?.okta?.issuer_url
          const loyaltyClientId = configData?.foreign_auth_providers?.okta?.client_id
          commit('SET_LOYALTY_BASE_URL', loyaltyBaseUrl);
          commit('SET_LOYALTY_CLIENT_ID', loyaltyClientId);
          // go through the services and find the first one that is not require address and is visible
          let service = services.find((service) => !service.requires_address && service.is_visible)
          if (this.state?.branding?.display_options?.make_default_service_type_first && service) {
            // move it to the top of the list
            services.splice(services.indexOf(service), 1)
            services.unshift(service)
          }
          commit('SET_STORE_CONFIG', configData)
          commit('SET_COOKIES', response.data.cookies)

          if (localStorage.getItem('selectedServiceId') === null && services.length > 0) {
            let routeName = router.currentRoute.value.name
            if (
              (service &&
                (routeName == 'HomeView' ||
                  routeName == 'PasswordResetView' ||
                  routeName == 'LoginView' ||
                  routeName == 'SignUpView')) ||
              routeName == 'ConfirmationView'
            ) {
              localStorage.setItem('selectedServiceId', service.service_type_id)
              commit('SET_SELECTED_SERVICE', service)
            }
            // if there is no service that does not require address, fall back to the default
            // one — see pickDefaultService.
            const fallbackService = !service && pickDefaultService(services)
            if (fallbackService) {
              localStorage.setItem('selectedServiceId', fallbackService.service_type_id)
              commit('SET_SELECTED_SERVICE', fallbackService)
            }
          }
          // Only attempt to hydrate selected service/store if there are services configured
          if (services.length > 0) {
            dispatch('getSelectedService')
            if (!services[0].requires_address) {
              dispatch('getSelectedStore')
            }
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    getSelectedStore({ commit }) {
      if (this.state.selectedStore) return
      let storeId = localStorage.getItem('selectedStoreId')
      let payload = {
        service_schedule: true,
        service_type_id: localStorage.getItem('selectedServiceId'),
      }
      if (!storeId) return
      storesApi
        .getStore(storeId, payload)
        .then((response) => {
          commit('SET_SELECTED_STORE', response.data.data)
          commit('SET_COOKIES', response.data.cookies)
        })
        .catch((error) => {
          console.log(error)
        })
    },
    getSelectedService({ commit }) {
      if (this.state.selectedService) return
      let services = this.state.storeConfig?.services || []
      let selectedServiceId = localStorage.getItem('selectedServiceId')
      let selectedService = services.find((service) => service.service_type_id == selectedServiceId)
      // Fallback: when there is no stored service id (e.g. config resolved on a route
      // that skipped the default-service selection above, such as the content hub),
      // the find() above returns undefined. Committing undefined leaves the ordering
      // page with no selected service, so it never fetches locations until a manual
      // refresh. Pick a sensible default instead — see pickDefaultService.
      if (!selectedService) {
        selectedService = pickDefaultService(services)
      }
      if (!selectedService) return
      localStorage.setItem('selectedServiceId', selectedService.service_type_id)
      commit('SET_SELECTED_SERVICE', selectedService)
    },
    getOrderData({ commit }) {
      if (this.state.orderData || localStorage.getItem('orderId') === null) return
      let orderId = localStorage.getItem('orderId')
      ordersApi
        .getOrder(orderId)
        .then((response) => {
          commit('SET_ORDER_DATA', response.data.data)
          commit('SET_COOKIES', response.data.cookies)
        })
        .catch((error) => {
          console.log(error)
          if (error.response && error.response.status == 403) {
            let toasterData = {
              show: true,
              message: i18n.global.t('common.sessionExpiredCartCleared'),
            }
            commit('SET_TOASTER', toasterData)
            commit('PURGE_STALE_DATA')
            commit('RESET_COOKIES')
            router.push('/')
          }
        })
    },
    getCustomerData({ commit }) {
      if (this.state.customerData && this.state.loggedIn) return
      customersApi
        .getCustomerInfo()
        .then((response) => {
          commit('SET_CUSTOMER_DATA', response.data.data)
          commit('SET_COOKIES', response.data.cookies)

          // Identify user in Coralogix when customer data is restored
          if (response.data.data && response.data.data.customer_id) {
            try {
              const fullName = response.data.data.first_name + ' ' + response.data.data.last_name
              CoralogixRum.setUserContext({
                user_email: response.data.data.email || '',
                user_name: fullName,
                user_id: response.data.data.customer_id
              })
            } catch (error) {
              console.log('Coralogix user identification error during session restoration:', error)
            }
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    async getBranding({ commit, dispatch }) {
      if (this.state.branding) return
      await storesApi
        .getBranding()
        .then((response) => {
          // Tenants vary in nesting; after httpClient normalization most responses are at `response.data.data`.
          // Some tenants may still wrap branding deeper.
          commit('SET_BRANDING', response?.data?.data?.data || response?.data?.data)
          // if data is missing, set it to the default
          dispatch('setMissingBranding')
        })
        .catch((error) => {
          console.log(error)
          commit('SET_BRANDING', brandingDefault)
        })
    },
    setMissingBranding({ commit }) {
      const propertiesToUpdate = [
        { key: 'primary_color', value: brandingDefault.primary_color },
        { key: 'primary_text_color', value: brandingDefault.primary_text_color },
        { key: 'background_color', value: brandingDefault.background_color },
        { key: 'gray_color', value: brandingDefault.gray_color },
        { key: 'passive_color', value: brandingDefault.passive_color },
        { key: 'logo_url', value: brandingDefault.logo_url },
        { key: 'hero_image_url', value: brandingDefault.hero_image_url },
        { key: 'gift_card_image', value: brandingDefault.gift_card_image },
        { key: 'favicon_url', value: brandingDefault.favicon_url },
        { key: 'menu_logo_url', value: brandingDefault.menu_logo_url },
        { key: 'fonts', value: brandingDefault.fonts },
        { key: 'title', value: brandingDefault.title },
        { key: 'description', value: brandingDefault.description },
        { key: 'markers', value: brandingDefault.markers },
        { key: 'map_style', value: brandingDefault.map_style },
        { key: 'links', value: brandingDefault.links },
        { key: 'category_placement', value: brandingDefault.category_placement },
        { key: 'donation', value: brandingDefault.donation },
        { key: 'display_options.rewards', value: brandingDefault.display_options.rewards },
        { key: 'display_options.category_image', value: brandingDefault.display_options.category_image },
        { key: 'display_options.icons', value: brandingDefault.display_options.icons },
        { key: 'display_options.hide_order_note', value: brandingDefault.display_options.hide_order_note },
        { key: 'display_options.hide_item_note', value: brandingDefault.display_options.hide_item_note },
        { key: 'display_options.display_rewards_page', value: brandingDefault.display_options.display_rewards_page },
        {
          key: 'display_options.display_promos_and_rewards_on_cart',
          value: brandingDefault.display_options.display_promos_and_rewards_on_cart,
        },
        {
          key: 'display_options.display_partial_payments',
          value: brandingDefault.display_options.display_partial_payments,
        },
        { key: 'display_options.template', value: brandingDefault.display_options.template },
        {
          key: 'display_options.display_order_tracking',
          value: brandingDefault.display_options.display_order_tracking,
        },
        { key: 'display_options.button_radius', value: brandingDefault.display_options.button_radius },
        {
          key: 'display_options.display_store_images_as_hero',
          value: brandingDefault.display_options.display_store_images_as_hero,
        },
        { key: 'display_options.hide_include_utensils', value: brandingDefault.display_options.hide_include_utensils },
        { key: 'display_options.hide_order_status', value: brandingDefault.display_options.hide_order_status },
        {
          key: 'display_options.change_order_cta_text',
          value: brandingDefault.display_options.change_order_cta_text,
        },
        {
          key: 'display_options.order_now_cta_text',
          value: brandingDefault.display_options.order_now_cta_text,
        },
        {
          key: 'display_options.order_ahead_cta_text',
          value: brandingDefault.display_options.order_ahead_cta_text,
        },
        {
          key: 'display_options.display_delivery_instructions',
          value: brandingDefault.display_options.display_delivery_instructions,
        },
        {
          key: 'display_options.display_gift_cards_in_profile',
          value: brandingDefault.display_options.display_gift_cards_in_profile,
        },
        {
          key: 'display_options.colour_fill_item_modifier_box_on_select',
          value: brandingDefault.display_options.colour_fill_item_modifier_box_on_select,
        },
        {
          key: 'display_options.additional_checkbox_options_enabled',
          value: brandingDefault.display_options.additional_checkbox_options_enabled,
        },
        {
          key: 'display_options.additional_checkbox_options',
          value: brandingDefault.display_options.additional_checkbox_options,
        },
      ]

      propertiesToUpdate.forEach((property) => {
        if (property.key.includes('.')) {
          let keys = property.key.split('.')
          if (!this.state.branding[keys[0]]) {
            commit('SET_BRANDING_DEFAULT', { key: 'display_options', value: {} })
          }
          if (!this.state.branding[keys[0]][keys[1]]) {
            commit('SET_BRANDING_DEFAULT', { key: property.key, value: property.value })
          }
        } else {
          if (!this.state.branding[property.key]) {
            commit('SET_BRANDING_DEFAULT', { key: property.key, value: property.value })
          }
        }
      })
    },
    setFraudSessionID() {
      let merchantId = localStorage.getItem('merchantId')
      if (merchantId) {
        window.Finix.Auth('live', merchantId, (sessionKey) => {
          localStorage.setItem('fraudSessionId', sessionKey)
        });
      }
      if (sessionStorage.getItem('fraudSessionID')) return
    },
  },
  modules: {
    risk,
  },
})
